import { getStorage, ref, getDownloadURL } from 'firebase/storage';

// Cache for processed URLs to avoid repeated processing
const urlCache = new Map<string, string>();

/**
 * Convert a Firebase Storage URL to a properly formatted download URL
 * This handles CORS issues and ensures proper authentication
 */
export const getAuthenticatedImageUrl = async (firebaseUrl: string): Promise<string> => {
  try {
    // Check cache first
    if (urlCache.has(firebaseUrl)) {
      return urlCache.get(firebaseUrl)!;
    }

    // If URL is already properly formatted (has alt=media), clean it up
    if (firebaseUrl.includes('firebasestorage.googleapis.com')) {
      // Remove port if present and ensure proper format
      let cleanUrl = firebaseUrl.replace(':443', '');
      
      // If it already has alt=media, use it directly
      if (cleanUrl.includes('alt=media')) {
        urlCache.set(firebaseUrl, cleanUrl);
        return cleanUrl;
      }
      
      // Extract the path from the URL to get a proper reference
      const urlParts = cleanUrl.split('/o/')[1];
      if (urlParts) {
        const pathPart = urlParts.split('?')[0];
        const decodedPath = decodeURIComponent(pathPart);
        
        // Get a fresh download URL using Firebase SDK
        const storage = getStorage();
        const imageRef = ref(storage, decodedPath);
        const downloadUrl = await getDownloadURL(imageRef);
        
        urlCache.set(firebaseUrl, downloadUrl);
        return downloadUrl;
      }
    }
    
    // Fallback: return original URL
    return firebaseUrl;
  } catch (error) {
    console.error('Error processing image URL:', error);
    // Return original URL as fallback
    return firebaseUrl;
  }
};

/**
 * Preprocess image URLs to fix common issues
 */
export const preprocessImageUrl = (url: string): string => {
  if (!url) return url;
  
  // Remove port from Firebase Storage URLs
  return url.replace('firebasestorage.googleapis.com:443', 'firebasestorage.googleapis.com');
};
